// Opt in for this browser tab with sessionStorage.setItem('storePerformance', '1').
// Timings stay in the browser Performance panel; only the latest measure per phase is kept.
let nextTimingId = 0;
const noop = (): void => {};

export function startStoreTiming(phase: string): () => void {
  try {
    if (typeof window === 'undefined' || window.sessionStorage.getItem('storePerformance') !== '1') return noop;
    const perf = window.performance;
    const label = `store.${phase}`;
    const start = `${label}.${nextTimingId++}.start`;
    const end = `${start}.end`;
    perf.mark(start);
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      try {
        perf.mark(end);
        perf.clearMeasures(label);
        perf.measure(label, start, end);
      } catch {
        // Diagnostics must never interrupt ordering on unsupported browsers.
      } finally {
        try {
          perf.clearMarks(start);
          perf.clearMarks(end);
        } catch {
          // Cleanup is also best-effort when Performance APIs are unavailable.
        }
      }
    };
  } catch {
    // Storage may be unavailable (for example in a restricted browser context).
    return noop;
  }
}

export function measureStoreSync<T>(phase: string, work: () => T): T {
  const finish = startStoreTiming(phase);
  try {
    return work();
  } finally {
    finish();
  }
}

export async function measureStoreAsync<T>(phase: string, work: () => Promise<T>): Promise<T> {
  const finish = startStoreTiming(phase);
  try {
    return await work();
  } finally {
    finish();
  }
}

/** Local, opt-in request diagnostics. Never retain URLs, query parameters or headers. */
export async function measureStoreRequest<T>(work: () => Promise<T>): Promise<T> {
  let recordResponse: (response: any) => void = noop;
  try {
    if (typeof window !== 'undefined' && window.sessionStorage.getItem('storePerformance') === '1') {
      const perf = window.performance;
      const started = perf.now();
      recordResponse = response => {
        try {
          const body = response?.body;
          const bodyBytes = body instanceof ArrayBuffer || ArrayBuffer.isView(body) ? body.byteLength : undefined;
          const resource = response?.url && perf.getEntriesByName(response.url, 'resource')
            .filter(entry => entry.startTime >= started).pop() as PerformanceResourceTiming;
          // Cross-origin timing fields can be withheld. Do not report their zeros
          // as a fast network/server response, or infer server processing time.
          const networkTimingAvailable = !!resource && resource.requestStart > 0 && resource.responseStart >= resource.requestStart;
          const detail = {
            bodyBytes,
            networkTimingAvailable,
            ...(networkTimingAvailable ? {
              beforeRequestMs: resource.requestStart - resource.startTime,
              responseWaitMs: resource.responseStart - resource.requestStart,
              responseDownloadMs: resource.responseEnd - resource.responseStart
            } : {})
          };
          const label = 'store.request-details';
          perf.clearMeasures(label);
          const now = perf.now();
          perf.measure(label, { start: now, end: now, detail });
        } catch {
          // Optional Resource Timing or measure detail support may be unavailable.
        }
      };
    }
  } catch {
    // Storage/performance restrictions must not change the request.
  }
  const response = await measureStoreAsync('request', work);
  recordResponse(response);
  return response;
}
